import React from "react";
import {
  alpha,
  Box,
  FormControlLabel,
  Paper,
  Switch,
  Typography,
  useTheme,
} from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";

/**
 * Reusable config toggle card with gradient background, icon, switch,
 * status label, and description.
 *
 * @param {object}  props
 * @param {React.ElementType} props.icon          – MUI icon component shown when active
 * @param {React.ElementType} [props.inactiveIcon] – MUI icon shown when inactive (defaults to BlockIcon)
 * @param {string}  props.title                   – Card title
 * @param {string}  props.description              – Description text for the current state
 * @param {boolean} props.isActive                 – Whether the config is currently enabled
 * @param {function} props.onChange                – Called with the change event when the switch is toggled
 * @param {string}  [props.activeLabel="Ativado"]  – Label shown when active
 * @param {string}  [props.inactiveLabel="Desativado"] – Label shown when inactive
 * @param {string}  [props.activeColor="success"]  – Palette key for the active state color
 * @param {string}  [props.inactiveColor="warning"] – Palette key for the inactive state color
 * @param {object}  [props.sx]                     – Extra sx passed to the root Paper
 * @param {React.ReactNode} [props.children]       – Optional extra content rendered below the description
 */
export default function ConfigSwitchCard({
  icon: Icon,
  inactiveIcon: InactiveIcon = BlockIcon,
  title,
  description,
  isActive,
  onChange,
  activeLabel = "Ativado",
  inactiveLabel = "Desativado",
  activeColor = "success",
  inactiveColor = "warning",
  sx = {},
  children,
}) {
  const theme = useTheme();

  const currentColor = isActive
    ? theme.palette[activeColor].main
    : theme.palette[inactiveColor].main;
  const currentColorLight = isActive
    ? theme.palette[activeColor].light
    : theme.palette[inactiveColor].light;
  const DisplayIcon = isActive ? Icon : InactiveIcon;

  return (
    <Paper
      elevation={3}
      sx={{
        mb: 3,
        overflow: "hidden",
        borderRadius: 2,
        transition: "all 0.3s ease",
        border: "1px solid",
        borderColor: alpha(currentColor, 0.3),
        ...sx,
      }}
    >
      <Box sx={{ position: "relative", overflow: "hidden" }}>
        {/* Background gradient */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            background: `linear-gradient(45deg, ${currentColorLight}, ${currentColor})`,
            zIndex: 0,
          }}
        />

        <Box sx={{ position: "relative", zIndex: 1, p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(currentColor, 0.1),
                color: currentColor,
                borderRadius: 1.5,
                p: 0.75,
                width: 36,
                height: 36,
                mr: 2,
              }}
            >
              <DisplayIcon fontSize="small" />
            </Box>

            <Typography variant="h6" fontWeight={500}>
              {title}
            </Typography>

            <Box sx={{ ml: "auto" }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={onChange}
                    color={isActive ? activeColor : inactiveColor}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: currentColor,
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor: currentColor,
                        },
                    }}
                  />
                }
                label=""
              />
            </Box>
          </Box>

          <Box sx={{ ml: 7, mb: 1 }}>
            <Typography
              variant="subtitle1"
              fontWeight={500}
              color={isActive ? `${activeColor}.main` : `${inactiveColor}.main`}
            >
              {isActive ? activeLabel : inactiveLabel}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Box>

          {children}
        </Box>
      </Box>
    </Paper>
  );
}
